import React, {
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';

import {
	getAgencyLocation,
	getAgencyLocationList,
	deleteAgencyLocation
} from '../../../../../../api/nora/agencies';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import {
	useAgencyState,
	useAgencyAction
} from '../../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Alert from '../../../../../Alert';
import HelpModal from '../../../../../Shared/HelpModal';
import LocationServices from '../../../../../Table/Cells/LocationServices';
import NameAndAddress from '../../../../../Table/Cells/NameAndAddress';
import SectionHeader from '../../../../../Shared/SectionHeader';
import Table from '../../../../../Table';
import TableRowActions from '../../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../../Table/filterFunctions/multiWordFilterFn';
import LocationsModal from './LocationsModal';
import { Loader } from 'semantic-ui-react';

const LocationTab = () => {
	const { user: { permissions } } = useAuthState();
	const { agency } = useAgencyState();
	const { setAgencyLocations } = useAgencyAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ location, setLocation ] = useState( null );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ locationToDelete, setLocationToDelete ] = useState( {} );
	const [ isLoadingLocations, setIsLoadingLocations ] = useState( false );

	const retrieveLocationList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Location List...' );
		setIsLoadingLocations( true );

		try {
			const data = await getAgencyLocationList( agency?.id );

			setAgencyLocations( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
		setIsLoadingLocations( false );
	}, [
		setInfobarLoader,
		agency,
		setAgencyLocations,
		setApiErrorAlert
	] );

	const addLocation = useCallback( () => {
		setLocation( {} );
	}, [] );

	const locationActions = useMemo( () => [
		{
			action: addLocation,
			icon: 'add',
			permission: true,
			text: 'Add Location'
		}
	], [ addLocation ] );

	const closeLocationModal = useCallback( () => {
		setLocation( null );
	}, [] );

	const nameAndAddressCell = useCallback( ( { row } ) => <NameAndAddress row={ row.original } />, [] );
	const servicesCell = useCallback( ( { row } ) => <LocationServices row={ row.original } />, [] );

	const onView = useCallback( async ( { id } ) => {
		setInfobarLoader( 'Retrieving Location Info...' );

		try {
			const location = await getAgencyLocation( agency.id, id );

			setLocation( location );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		agency?.id,
		setApiErrorAlert,
		setInfobarLoader
	] );

	const onDelete = useCallback( location => {
		setLocationToDelete( location );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.editAgencies.canExecute,
				icon: 'search',
				permission: permissions.editAgencies.isAvailable,
				text: 'View Location'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editAgencies.canExecute,
				icon: 'times',
				permission: permissions.editAgencies.isAvailable,
				text: 'Remove Location'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.name }${ row.address }${ row.address2 }${ row.city }${ row.state }${ row.zip }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: nameAndAddressCell,
			minWidth: 60
		},
		{
			id: 'services',
			accessor: row => `${ row.services }`,
			Header: 'Services',
			// filter: multiWordFilterFn,
			Cell: servicesCell,
			minWidth: 60
		}
	], [ nameAndAddressCell, servicesCell ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewUsers.isAvailable || permissions.editAgencies.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const handleDeleteLocation = useCallback( async () => {
		setInfobarLoader( 'Retrieving Location List...' );

		try {
			await deleteAgencyLocation( agency?.id, locationToDelete.id );
			await retrieveLocationList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		agency,
		locationToDelete.id,
		retrieveLocationList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ `${ locationToDelete.name }` }</strong> from the list?</span>, [ locationToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<>
			<SectionHeader
				actions={ locationActions }
				title={
					<>
						<span>Agency Locations</span>
						<HelpModal
							id={ 29 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ isLoadingLocations
				? <Loader active />
				: agency?.locations?.length
					? <Table
						columns={ renderColumns }
						data={ agency.locations }
						showCount={ agency.locations.length > 10 }
						showEmptyRows={ false }
						showPagination={ agency.locations.length > 10 }
					/>
					: <p>No locations found.</p>
			}
			<LocationsModal
				closeModal={ closeLocationModal }
				location={ location }
				onSave={ retrieveLocationList }
				open={ Boolean( location ) }
			/>
			<Alert
				action={ handleDeleteLocation }
				name="unassign-coordination-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</>
	);
};

LocationTab.propTypes = { activeIndex: PropTypes.number };

export default LocationTab;
